/**
 * 表セクションの読み取り。
 * templateNodes から列/行/セルなどのread modelを取り出すだけで、新しい state は作らない。
 */

import {
  TemplateNodeSchema,
  TemplateNodeTypeEnum,
} from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'

/**
 * 表の列を取得する。
 * 表直下に列ノードは存在しない。列の定義は「テンプレート行」が持つ質問群であり、
 * その i 番目の質問が i 列目に対応する(全行は同一の質問構造を持つ)。
 */
export const getTableColumnNodes = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema[] => {
  const templateRow = getTableTemplateRowNode(tableNode, templateNodes)
  return getRowCellQuestions(templateRow, templateNodes)
}

export const getTableRowSectionNodes = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema[] =>
  tableNode.nodes
    .map(id => templateNodes[id])
    .filter(n => n !== undefined)
    .filter(n => n.type === TemplateNodeTypeEnum.Section)

/**
 * 記録時に行を追加するための「テンプレート行」(`isRepeat = 1`)かどうか。
 * 表は末尾にこのテンプレート行を常に1つ持ち、編集UIの固定行表示からは除外する。
 */
export const isTableTemplateRow = (node: TemplateNodeSchema): boolean =>
  node.type === TemplateNodeTypeEnum.Section && node.section?.isRepeat === 1

/** 固定行(`isRepeat = 0`)のみを取得する。編集UIで表示・編集する行。 */
export const getTableFixedRowSectionNodes = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema[] =>
  getTableRowSectionNodes(tableNode, templateNodes).filter(
    row => !isTableTemplateRow(row),
  )

/** テンプレート行(`isRepeat = 1`)のみを取得する。 */
export const getTableTemplateRowNode = (
  tableNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema => {
  const rows = getTableRowSectionNodes(tableNode, templateNodes).filter(
    isTableTemplateRow,
  )

  if (rows.length === 0) {
    throw new Error('表の中にテンプレート行が存在しません')
  }

  if (rows.length > 1) {
    throw new Error('表の中にテンプレート行が複数存在します')
  }

  const row = rows[0]
  if (!row) {
    throw new Error('表の中にテンプレート行が存在しません')
  }
  return row
}

export const getRowCellQuestions = (
  rowSection: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema[] =>
  rowSection.nodes
    .map(id => templateNodes[id])
    .filter(n => n !== undefined)
    .filter(n => n.type === TemplateNodeTypeEnum.Question)

export const getCellQuestionForColumnIndex = (
  rowSection: TemplateNodeSchema,
  columnIndex: number,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema | undefined =>
  getRowCellQuestions(rowSection, templateNodes)[columnIndex]

export const getQuestionLogicNodes = (
  questionNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema[] =>
  questionNode.nodes
    .map(id => templateNodes[id])
    .filter(n => n !== undefined)
    .filter(n => n.type === TemplateNodeTypeEnum.Logic)

/** 条件分岐が持つ分岐質問を取得する。プレビューでは横方向の列として展開する。 */
export const getLogicBranchQuestions = (
  logicNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): TemplateNodeSchema[] =>
  logicNode.nodes
    .map(id => templateNodes[id])
    .filter(n => n !== undefined)
    .filter(n => n.type === TemplateNodeTypeEnum.Question)

/**
 * 指定ノードとその子孫すべての id を集める(列/分岐質問の削除時にサブツリーを回収するため)。
 * 列は子に 条件分岐→分岐質問のツリーを持つので、列を消すときは子孫も一緒に消す必要がある。
 */
export const collectSubtreeNodeIds = (
  rootId: number,
  templateNodes: TemplateNodesDict,
): number[] => {
  const result: number[] = []
  const visit = (id: number) => {
    const node = templateNodes[id]
    if (!node) {
      return
    }
    result.push(id)
    node.nodes.forEach(visit)
  }
  visit(rootId)
  return result
}
